import React, { useRef } from "react";
import { RichUtils, EditorState, AtomicBlockUtils } from "draft-js";

const ToolBarAddNewTask = ({ editorState, onChange, editorRef }) => {
  const applyStyle = (style) => {
    const newState = RichUtils.toggleInlineStyle(editorState, style);
    onChange(newState);
    // Focus after a short delay to ensure the UI updates correctly
    setTimeout(() => {
      if (editorRef?.current) editorRef.current.focus();
    }, 0);
  };

  const applyBlockType = (blockType) => {
    const newState = RichUtils.toggleBlockType(editorState, blockType);
    onChange(newState);
    setTimeout(() => {
      if (editorRef?.current) editorRef.current.focus();
    }, 0);
  };

  const addLink = (e) => {
    e.preventDefault();
    const selection = editorState.getSelection();

    if (!selection.isCollapsed()) {
      const link = prompt("Enter URL");
      if (link) {
        // Ensure the URL has a protocol
        const formattedUrl =
          link.startsWith("http://") || link.startsWith("https://")
            ? link
            : `https://${link}`;

        const contentState = editorState.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity(
          "LINK",
          "MUTABLE",
          { url: formattedUrl },
        );
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        const newEditorState = EditorState.set(editorState, {
          currentContent: contentStateWithEntity,
        });

        const newStateWithLink = RichUtils.toggleLink(
          newEditorState,
          newEditorState.getSelection(),
          entityKey,
        );

        onChange(newStateWithLink);

        // Focus after applying the link
        setTimeout(() => {
          if (editorRef?.current) editorRef.current.focus();
        }, 0);
      }
    } else {
      alert("Please select some text first");
    }
  };

  // Function to remove link
  const removeLink = (e) => {
    e.preventDefault();
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      onChange(RichUtils.toggleLink(editorState, selection, null));
    }
  };

  // Check if current selection has a link
  const hasLink = () => {
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const startKey = selection.getStartKey();
    const startOffset = selection.getStartOffset();
    const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
    const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);

    if (linkKey) {
      const linkInstance = contentState.getEntity(linkKey);
      return linkInstance.getType() === "LINK";
    }
    return false;
  };

  //   insert image
  const fileInput = useRef(null);
  const handleImageUpload = (e) => {
    const file = e.target.files[0]; // Get the selected file
    if (file) {
      const reader = new FileReader();

      reader.onloadend = () => {
        const imageUrl = reader.result; // This is the base64 URL

        // Now we can insert the image into the editor
        const contentState = editorState.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity(
          "IMAGE",
          "IMMUTABLE",
          { src: imageUrl },
        );
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        const newEditorState = EditorState.set(editorState, {
          currentContent: contentStateWithEntity,
        });

        onChange(
          AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, " "),
        );
      };

      reader.readAsDataURL(file); // Read the file as a base64 URL
    }
  };

  //   insert hr line
  const addHorizontalLine = (e) => {
    e.preventDefault();

    // Create a custom entity for the horizontal line
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      "HR", // The entity type (you can name this anything you like)
      "IMMUTABLE", // It doesn't change, so we use 'IMMUTABLE'
      {}, // Empty data object as we don't need extra data
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

    const newEditorState = EditorState.set(editorState, {
      currentContent: contentStateWithEntity,
    });

    onChange(
      AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, " "),
    );
  };

  //   click handle
  const handleButtonClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    return false;
  };

  // Check if current block has a specific type
  const blockType = () => {
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    return contentState.getBlockForKey(selection.getStartKey()).getType();
  };

  // Check if current selection has a specific inline style
  const hasInlineStyle = (style) => {
    return editorState.getCurrentInlineStyle().has(style);
  };

  return (
    <div
      className="toolbar"
      style={{
        height: "45px",
        display: "flex",
        gap: "10px",
        padding: "10px",
        borderTopLeftRadius: "12px",
        borderTopRightRadius: "12px",
        border: "1px solid #273b4b ",
        backgroundColor: "#132534 ",
      }}
    >
      {/* Inline Styles */}
      <button
        onMouseDown={(e) => {
          handleButtonClick(e);
          applyStyle("BOLD");
        }}
        title="Bold"
        style={{
          height: "20px",
          width: "20px",
          backgroundImage: "url('icons/text-editor-bold.svg')",
          backgroundRepeat: "no-repeat",
          backgroundSize: "20px 20px",
          border: "none",
          backgroundColor: hasInlineStyle("BOLD") ? "#273b4b" : "transparent",
          cursor: "pointer",
        }}
      ></button>
      {/* Rest of the buttons - changed onClick to onMouseDown for all */}
      <button
        onMouseDown={(e) => {
          handleButtonClick(e);
          applyStyle("ITALIC");
        }}
        title="Italic"
        style={{
          height: "20px",
          width: "20px",
          backgroundImage: "url('icons/text-editor-italic.svg')",
          backgroundRepeat: "no-repeat",
          backgroundSize: "20px 20px",
          border: "none",
          backgroundColor: hasInlineStyle("ITALIC") ? "#273b4b" : "transparent",
          cursor: "pointer",
        }}
      ></button>
      <button
        onMouseDown={(e) => {
          handleButtonClick(e);
          applyStyle("UNDERLINE");
        }}
        title="Underline"
        style={{
          height: "20px",
          width: "20px",
          backgroundImage: "url('icons/text-editor-underline.svg')",
          backgroundRepeat: "no-repeat",
          backgroundSize: "20px 20px",
          border: "none",
          backgroundColor: hasInlineStyle("UNDERLINE")
            ? "#273b4b"
            : "transparent",
          cursor: "pointer",
        }}
      ></button>
      <button
        onMouseDown={(e) => {
          handleButtonClick(e);
          applyStyle("STRIKETHROUGH");
        }}
        title="Strikethrough"
        style={{
          height: "20px",
          width: "20px",
          backgroundImage: "url('icons/text-editor-line-through.svg')",
          backgroundRepeat: "no-repeat",
          backgroundSize: "20px 20px",
          border: "none",
          backgroundColor: hasInlineStyle("STRIKETHROUGH")
            ? "#273b4b"
            : "transparent",
          cursor: "pointer",
        }}
      ></button>
      <button
        onMouseDown={(e) => {
          handleButtonClick(e);
          applyStyle("CODE");
        }}
        title="Code View"
        style={{
          height: "20px",
          width: "20px",
          backgroundImage: "url('icons/text-editor-code.svg')",
          backgroundRepeat: "no-repeat",
          backgroundSize: "20px 20px",
          border: "none",
          backgroundColor: hasInlineStyle("CODE") ? "#273b4b" : "transparent",
          cursor: "pointer",
        }}
      ></button>

      {/* Lists */}
      <button
        onMouseDown={(e) => {
          handleButtonClick(e);
          applyBlockType("unordered-list-item");
        }}
        title="Unordered List"
        style={{
          height: "20px",
          width: "20px",
          backgroundImage: "url('icons/text-editor-ul.svg')",
          backgroundRepeat: "no-repeat",
          backgroundSize: "20px 20px",
          border: "none",
          backgroundColor:
            blockType() === "unordered-list-item" ? "#273b4b" : "transparent",
          cursor: "pointer",
        }}
      ></button>
      <button
        onMouseDown={(e) => {
          handleButtonClick(e);
          applyBlockType("ordered-list-item");
        }}
        title="Ordered List"
        style={{
          height: "20px",
          width: "20px",
          backgroundImage: "url('icons/text-editor-ol.svg')",
          backgroundRepeat: "no-repeat",
          backgroundSize: "20px 20px",
          border: "none",
          backgroundColor:
            blockType() === "ordered-list-item" ? "#273b4b" : "transparent",
          cursor: "pointer",
        }}
      ></button>

      {/* Insert Elements */}
      <button
        onMouseDown={hasLink() ? removeLink : addLink}
        title={hasLink() ? "Remove Link" : "Insert Link"}
        style={{
          height: "20px",
          width: "20px",
          backgroundImage: "url('icons/text-editor-link.svg')",
          backgroundRepeat: "no-repeat",
          backgroundSize: "20px 20px",
          border: "none",
          backgroundColor: hasLink() ? "#273b4b" : "transparent",
          cursor: "pointer",
        }}
      ></button>

      {/* image */}
      <button
        onMouseDown={(e) => fileInput.current.click()}
        title="Insert Image"
        style={{
          height: "20px",
          width: "20px",
          backgroundImage: "url('icons/text-editor-image.svg')",
          backgroundRepeat: "no-repeat",
          backgroundSize: "20px 20px",
          border: "none",
          backgroundColor: "transparent",
          cursor: "pointer",
        }}
      ></button>
      <input
        ref={fileInput}
        type="file"
        style={{ display: "none" }}
        accept="image/*"
        onChange={handleImageUpload}
      />

      {/* insert hr */}
      <button
        onMouseDown={addHorizontalLine}
        title="Insert Hr"
        style={{
          height: "20px",
          width: "20px",
          backgroundImage: "url('icons/text-editor-hr.svg')",
          backgroundRepeat: "no-repeat",
          backgroundSize: "20px 20px",
          border: "none",
          backgroundColor: "transparent",
          cursor: "pointer",
        }}
      ></button>
    </div>
  );
};

export default ToolBarAddNewTask;
